import { useCallback, useEffect, useReducer, useRef } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';
import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';
import { EntityAvatar } from '@/modules/common';

import { SingleSelectSearchInput } from './single-select-search-input';
import { SingleSelectOption } from './single-select-types';

interface SingleSelectVirtualizedListProps {
  options: SingleSelectOption[];
  selectedValue: string;
  onSelectOption: (value: string) => void;
  showAvatars: boolean;
  showDescriptionInOptions: boolean;
  avatarShape: 'circle' | 'square' | 'rounded';
  avatarSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  maxHeight: string;
  emptyMessage: string;
  searchable: boolean;
  hideSearchWhenEmpty: boolean;
  searchValue: string;
  searchPlaceholder: string;
  onSearchChange: (value: string) => void;
  isOpen: boolean;
  /**
   * Optional custom renderer — must produce the whole row's contents
   * (selection indicator, avatar/decorations, label). When provided, the
   * built-in Check + EntityAvatar + label markup is skipped.
   */
  renderOption?: (option: SingleSelectOption, isSelected: boolean) => React.ReactNode;
}

export function SingleSelectVirtualizedList({
  options,
  selectedValue,
  onSelectOption,
  showAvatars,
  showDescriptionInOptions,
  avatarShape,
  avatarSize,
  maxHeight,
  emptyMessage,
  searchable,
  hideSearchWhenEmpty,
  searchValue,
  searchPlaceholder,
  onSearchChange,
  isOpen,
  renderOption,
}: SingleSelectVirtualizedListProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 5,
  });

  // Force re-render when parentRef is attached and popover opens
  useEffect(() => {
    if (isOpen) {
      const rafId = requestAnimationFrame(() => {
        if (parentRef.current) {
          forceUpdate();
        }
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [isOpen]);

  // When rendered inside a Radix Dialog, react-remove-scroll blocks native wheel events
  // on portaled content (like Popover). We attach a native wheel listener in the capture
  // phase to scroll the container manually before the scroll lock can preventDefault.
  const wheelHandlerRef = useRef<((e: WheelEvent) => void) | null>(null);
  const setScrollRef = useCallback((node: HTMLDivElement | null) => {
    // Clean up previous listener
    if (parentRef.current && wheelHandlerRef.current) {
      parentRef.current.removeEventListener('wheel', wheelHandlerRef.current, true);
      wheelHandlerRef.current = null;
    }

    (parentRef as React.MutableRefObject<HTMLDivElement | null>).current = node;

    if (!node) return;

    const handleWheel = (e: WheelEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = node;
      const maxScroll = scrollHeight - clientHeight;
      if (maxScroll <= 0) return;

      const isAtTop = scrollTop <= 0 && e.deltaY < 0;
      const isAtBottom = scrollTop >= maxScroll && e.deltaY > 0;

      if (!isAtTop && !isAtBottom) {
        e.preventDefault();
        e.stopPropagation();
        node.scrollTop += e.deltaY;
      }
    };

    node.addEventListener('wheel', handleWheel, { capture: true, passive: false });
    wheelHandlerRef.current = handleWheel;
  }, []);

  return (
    <>
      {searchable && !(hideSearchWhenEmpty && options.length === 0) && (
        <SingleSelectSearchInput
          value={searchValue}
          placeholder={searchPlaceholder}
          onChange={onSearchChange}
        />
      )}
      <div
        ref={setScrollRef}
        className="overflow-auto p-1"
        style={{
          height:
            options.length === 0
              ? 'auto'
              : Math.min(options.length * 36 + 8, parseInt(maxHeight || '300')),
          maxHeight,
        }}
      >
        {options.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
        ) : (
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualizer.getVirtualItems().map(virtualItem => {
              const option = options[virtualItem.index];
              if (!option) return null;

              const isSelected = selectedValue === option.value;

              return (
                <div
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  ref={virtualizer.measureElement}
                  onClick={() => onSelectOption(option.value)}
                  className={cn(
                    !renderOption &&
                      'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                    !renderOption && isSelected && 'bg-accent text-accent-foreground',
                    option.disabled && 'pointer-events-none opacity-50'
                  )}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  {renderOption ? (
                    renderOption(option, isSelected)
                  ) : (
                    <>
                      {isSelected && <Check className="mr-2 h-4 w-4 text-primary" />}
                      {showAvatars && (
                        <EntityAvatar
                          image={option.avatar}
                          color={option.color}
                          name={option.label}
                          size={avatarSize ?? 'sm'}
                          shape={avatarShape}
                          className="mr-3 flex-shrink-0"
                        />
                      )}
                      {option.customContent ?? (
                        <div className="flex-1 truncate">
                          <div className="truncate">{option.label}</div>
                          {showDescriptionInOptions && option.description && (
                            <div className="truncate text-xs text-muted-foreground">
                              {option.description}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
